import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, MessageSquare, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyFeedback({ user }) {
  const { playerId } = useParams();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [playerName, setPlayerName] = useState("");

  const isCoach = user?.roles?.includes("academy_coach");
  const isParent = user?.roles?.includes("academy_parent");
  const isPlayer = user?.roles?.includes("academy_player");

  useEffect(() => {
    if (playerId) {
      fetchFeedback();
    } else if (isPlayer && user.academy_player_id) {
      // Player viewing their own feedback
      fetchPlayerFeedback(user.academy_player_id);
    }
  }, [playerId, user]);

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${API}/academy/my-children/${playerId}/feedback`, {
        withCredentials: true
      });
      setFeedback(response.data);
      
      // Get player name
      const childrenResponse = await axios.get(`${API}/academy/my-children`, {
        withCredentials: true
      });
      const child = childrenResponse.data.find(c => c.id === playerId);
      if (child) {
        setPlayerName(child.child_name);
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerFeedback = async (playerIdParam) => {
    try {
      const response = await axios.get(`${API}/academy/my-children/${playerIdParam}/feedback`, {
        withCredentials: true
      });
      setFeedback(response.data);
      setPlayerName(user.name);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    
    if (!feedbackText.trim()) {
      toast.error("Please enter feedback text");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/academy/feedback`,
        {
          player_id: playerId,
          feedback_text: feedbackText
        },
        { withCredentials: true }
      );

      toast.success("Feedback submitted successfully!");
      setShowSubmitDialog(false);
      setFeedbackText("");
      fetchFeedback();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to={isParent ? "/academy/my-kids" : "/academy"}>
            <Button variant="ghost" className="text-white hover:text-purple-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-white">Coach Feedback</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-4xl font-bold text-white">Feedback</h2>
            {playerName && (
              <p className="text-slate-400 mt-2">For: {playerName}</p>
            )}
          </div>
          {isCoach && playerId && (
            <Button onClick={() => setShowSubmitDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Send className="w-4 h-4 mr-2" />
              Submit Feedback
            </Button>
          )}
        </div>

        {feedback.length === 0 ? (
          <Card className="p-8 text-center">
            <MessageSquare className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-400">No feedback yet.</p>
            {isCoach && (
              <p className="text-sm text-slate-400 mt-2">Be the first to provide feedback!</p>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {feedback.map((item) => (
              <Card key={item.id} className="p-6 bg-slate-900/50 border-2 border-purple-500/30">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-lg font-bold text-white">{item.coach_name}</p>
                    <p className="text-sm text-slate-400">Coach</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                    {item.session_id && (
                      <p className="text-xs text-slate-400">Session feedback</p>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-purple-500/10 rounded-lg">
                  <p className="text-white whitespace-pre-wrap">{item.feedback_text}</p>
                </div>

                {isParent && !item.parent_viewed && (
                  <div className="mt-3">
                    <span className="inline-block px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">
                      New
                    </span>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Submit Feedback Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Submit Feedback for {playerName}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={submitFeedback} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-300">Feedback *</label>
              <Textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Provide constructive feedback on the player's performance, attitude, skills, areas for improvement, etc."
                rows={6}
                required
              />
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowSubmitDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-purple-600 hover:bg-purple-700">
                {submitting ? "Submitting..." : "Submit Feedback"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
